import React, { useRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import FileUpload from '../molecules/FileUpload'
import RangeSlider from '../molecules/RangeSlider'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import Spinner from '../atoms/Spinner'
import { setOriginalFile, setCompressionLevel, setCompressing, setCompressedFile, setError, clearCompressorState } from '../store/slices/imageCompressorSlice'
import imageCompression from 'browser-image-compression'
import './ImageCompressorPage.css'

const ImageCompressorPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { originalFile, compressedFile, compressionLevel, originalSize, compressedSize, compressionRatio, isCompressing, error } = useSelector(state => state.imageCompressor)
  const [previewUrl, setPreviewUrl] = useState('')
  const [compressedPreviewUrl, setCompressedPreviewUrl] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  const handleFileSelect = (file) => {
    dispatch(setOriginalFile(file))
    if (file) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
      setCompressedPreviewUrl('')
    } else {
      setPreviewUrl('')
      setCompressedPreviewUrl('')
    }
  }

  const handleCompressionChange = (e) => {
    dispatch(setCompressionLevel(parseFloat(e.target.value) / 100))
  }

  const handleCompress = async () => {
    if (!originalFile) return

    dispatch(setCompressing(true))
    dispatch(setError(null))

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        quality: compressionLevel,
      }

      const compressedFile = await imageCompression(originalFile, options)
      dispatch(setCompressedFile(compressedFile))

      const compressedUrl = URL.createObjectURL(compressedFile)
      setCompressedPreviewUrl(compressedUrl)
    } catch (err) {
      dispatch(setError(err.message))
    } finally {
      dispatch(setCompressing(false))
    }
  }

  const handleDownload = () => {
    if (compressedFile) {
      const url = URL.createObjectURL(compressedFile)
      const a = document.createElement('a')
      a.href = url
      a.download = `compressed_${originalFile.name}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const handleClear = () => {
    dispatch(clearCompressorState())
    setPreviewUrl('')
    setCompressedPreviewUrl('')
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <ToolPageTemplate path="/image-compressor/" tools={tools}>
      <div className="image-compressor">
        <div className="compressor-input-section">
          {!originalFile ? (
            <FileUpload
              onFileSelect={handleFileSelect}
              accept="image/*"
              maxSize={10 * 1024 * 1024}
            >
              <div className="upload-content">
                <div className="upload-icon"></div>
                <h3>Upload Image</h3>
                <p>Drag and drop an image here or click to browse</p>
                <p className="upload-hint">Supports JPG, PNG, WebP (Max 10MB)</p>
              </div>
            </FileUpload>
          ) : (
            <div className="file-selected-info">
              <div className="selected-file-details">
                <h4>Selected Image:</h4>
                <p className="file-name">{originalFile.name}</p>
                <p className="file-size">
                  {(originalFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <Button variant="secondary" onClick={handleClear}>
                Choose Different Image
              </Button>
            </div>
          )}

          {originalFile && (
            <div className="compression-controls">
              <RangeSlider
                label="Compression Quality"
                value={compressionLevel * 100}
                onChange={(e) => handleCompressionChange(e)}
                min={10}
                max={100}
                step={5}
                unit="%"
              />

              <div className="compressor-buttons">
                <Button onClick={handleCompress} disabled={isCompressing}>
                  {isCompressing ? <Spinner size="small" /> : null}
                  Compress Image
                </Button>
                <Button variant="secondary" onClick={handleClear}>
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <ResultBox title="Error">
            <div className="error-message">{error}</div>
          </ResultBox>
        )}

        {originalFile && (
          <div className="image-preview-section">
            <div className="preview-grid">
              <div className="preview-item">
                <h4>Original</h4>
                <div className="preview-image">
                  <img src={previewUrl} alt="Original" />
                </div>
                <div className="preview-info">
                  <p><strong>Size:</strong> {formatFileSize(originalSize)}</p>
                  <p><strong>Name:</strong> {originalFile.name}</p>
                </div>
              </div>

              {compressedPreviewUrl && (
                <div className="preview-item">
                  <h4>Compressed</h4>
                  <div className="preview-image">
                    <img src={compressedPreviewUrl} alt="Compressed" />
                  </div>
                  <div className="preview-info">
                    <p><strong>Size:</strong> {formatFileSize(compressedSize)}</p>
                    <p><strong>Reduction:</strong> {compressionRatio}%</p>
                    <Button onClick={handleDownload} size="small">
                      Download
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <section className="tool-guide">
          {/* NEW: Definition section (was missing) */}
          <div className="guide-section">
            <h2>What Is Image Compression?</h2>
            <p>Image compression reduces the file size of a picture by removing redundant or imperceptible data while keeping it visually acceptable. A 4000×3000 photo from a phone camera is roughly 12–15 MB uncompressed — too large to load quickly on a website or attach to email. Compressed well, the same photo can drop below 500 KB with no visible difference on any screen.</p>
            <p>Two families of compression exist. <strong>Lossy compression</strong> (JPEG, WebP lossy) permanently discards some image data — mostly detail the human eye is unlikely to notice — in exchange for much smaller files. <strong>Lossless compression</strong> (PNG, WebP lossless) preserves every pixel exactly, producing larger files but zero quality loss. This tool uses lossy compression by default, since it's the right choice for photographs, hero images, and almost anything destined for the web.</p>
            <p>All compression happens entirely in your browser using the open-source browser-image-compression library. Your images are never uploaded, stored, or transmitted anywhere — you can verify this by opening your browser's Network tab and confirming no image data leaves the page.</p>
          </div>

          {/* NEW: Lossy vs Lossless comparison */}
          <div className="guide-section">
            <h2>Lossy vs Lossless Compression — Which to Use</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>Lossy (JPG, WebP)</h3>
                <p><strong>Best for:</strong> photographs, product images, hero banners, blog post images. Compression typically achieves 60–90% file size reduction. Quality is visually indistinguishable from the original at 80% or above for most images. This is what the slider above controls.</p>
              </div>
              <div className="use-case">
                <h3>Lossless (PNG, WebP lossless)</h3>
                <p><strong>Best for:</strong> logos, icons, screenshots with text, graphics with sharp edges or transparency. File sizes stay larger than lossy compression, but every pixel is preserved exactly. Not what this specific tool does — use our <Link to="/image-converter/">Image Converter</Link> for format changes that preserve lossless quality.</p>
              </div>
            </div>
          </div>

          {/* NEW: Compression savings by image type */}
          <div className="guide-section">
            <h2>Typical Compression Savings by Image Type</h2>
            <p>Compression results depend heavily on the image content, but these numbers reflect typical results users see when compressing at 80% quality:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Smartphone photo (JPG)</h3>
                <p><strong>Before:</strong> 4–6 MB · <strong>After:</strong> 300–500 KB · <strong>Savings:</strong> 85–92%</p>
              </div>
              <div className="use-case">
                <h3>Screenshot (PNG → compressed)</h3>
                <p><strong>Before:</strong> 800 KB–2 MB · <strong>After:</strong> 150–400 KB · <strong>Savings:</strong> 70–80%</p>
              </div>
              <div className="use-case">
                <h3>Product photo (JPG)</h3>
                <p><strong>Before:</strong> 2–3 MB · <strong>After:</strong> 200–400 KB · <strong>Savings:</strong> 80–90%</p>
              </div>
              <div className="use-case">
                <h3>Already-compressed image (JPG, low quality)</h3>
                <p><strong>Before:</strong> 200 KB · <strong>After:</strong> 180–200 KB · <strong>Savings:</strong> 0–10% — a second pass on an already-compressed file yields almost nothing, and can degrade quality further.</p>
              </div>
            </div>
          </div>

          {/* EXISTING: How to Use (kept, tightened) */}
          <div className="guide-section">
            <h2>How to Use the Image Compressor</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Upload your image</h3>
                  <p>Click the upload area or drag and drop a JPG, PNG, or WebP file up to 10MB. The tool validates the file type and size instantly.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Set the compression quality</h3>
                  <p>Use the quality slider from 10% to 100%. For web use, 80–90% is the sweet spot: nearly invisible quality loss, usually under 500 KB. Drop to 70% for maximum savings on non-critical images.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Compress and compare</h3>
                  <p>Click Compress Image. Side-by-side previews appear instantly, along with the exact percentage reduction in file size. If quality looks off, adjust the slider and re-compress — no upload limits.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Download the result</h3>
                  <p>Click Download to save the compressed image with a "compressed_" prefix, so your original stays untouched on your device.</p>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: When NOT to compress */}
          <div className="guide-section">
            <h2>When You Should NOT Compress an Image</h2>
            <p>Compression is almost always the right call for web images, but there are cases where it actively hurts you:</p>
            <p><strong>Already-compressed files.</strong> If an image has already passed through JPEG compression — a photo downloaded from a website, exported from a chat app, or received as a compressed attachment — running it through compression again throws away more data from an already-degraded file. The result is visible artifacts without meaningful size savings.</p>
            <p><strong>Images destined for print.</strong> Print requires far higher resolution and finer color gradients than screens. Compression artifacts that are invisible on a monitor become obvious on paper. Keep originals uncompressed for any print workflow.</p>
            <p><strong>Screenshots with fine text.</strong> JPEG compression blurs sharp edges, which is exactly what text is made of. Screenshots and text-heavy graphics should stay as PNG or use WebP lossless — never lossy JPEG compression.</p>
            <p><strong>Images you'll edit again.</strong> Every lossy compression pass is a permanent change. If you might crop, recolor, or adjust the image later, work from the original and compress once, as the final export step.</p>
          </div>

          {/* NEW: Common mistakes */}
          <div className="guide-section">
            <h2>Common Image Compression Mistakes</h2>
            <p><strong>Compressing to a fixed number instead of a target size.</strong> "Compress everything to 60% quality" is not a strategy — a photo with lots of sky compresses beautifully at 60%, while a portrait with fine skin texture may already look soft at 85%. Judge by the smoothest area of the image at 100% zoom, not by the slider number.</p>
            <p><strong>Never checking the compressed result at full size.</strong> Compression artifacts show up worst on large displays. A file that looks fine in a thumbnail preview may show blockiness at full resolution. Always zoom to 100% before downloading.</p>
            <p><strong>Compressing without resizing first.</strong> If an image will display at 800px wide, uploading a 4000px version and compressing it wastes bandwidth — the browser still downloads and then downsizes. Resize first, then compress, for the best combination of quality and size.</p>
            <p><strong>Skipping the format decision.</strong> JPEG, PNG, and WebP each behave differently under compression. Photographs → JPEG or WebP. Screenshots, logos, flat graphics with text → PNG or WebP lossless. Choosing the wrong format before compressing wastes effort — see our <Link to="/blog/image-format-guide-jpg-vs-png-vs-webp/">JPG vs PNG vs WebP guide</Link> for the full breakdown.</p>
          </div>

          {/* EXISTING: Why Use This Tool (rewritten — less generic, more specific) */}
          <div className="guide-section">
            <h2>Why Use This Compressor</h2>
            <p>This tool runs entirely in your browser, which matters for two practical reasons: your images never leave your device (no server log, no retention policy, no third party seeing your photos), and there's no upload wait — compression starts the moment you click the button, regardless of file size.</p>
            <p>It uses the same open-source <code>browser-image-compression</code> library used by production tools including several Google Cloud demos, so the output quality is not a hack or approximation. Side-by-side before/after previews let you verify the result before downloading, and because compression happens locally, you can iterate — adjust quality, re-compress, compare — as many times as you want.</p>
          </div>

          {/* NEW: Use cases */}
          <div className="guide-section">
            <h2>When to Use This Tool</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>Blog and website images</h3>
                <p>Hero images and inline content should stay under 200 KB for fast load times. Compressing at 80% typically achieves this without any visible quality change on the published page.</p>
              </div>
              <div className="use-case">
                <h3>Email attachments</h3>
                <p>Most email providers cap attachments at 25 MB, and many recipients on mobile have far lower tolerance. Compressing a batch of product photos from 15 MB to under 3 MB makes them reliably deliverable.</p>
              </div>
              <div className="use-case">
                <h3>E-commerce product photos</h3>
                <p>Product listings load faster — and convert better — when images are compressed. Shopify, Etsy, and Amazon all re-compress uploads, so starting with a smaller file avoids double compression artifacts.</p>
              </div>
              <div className="use-case">
                <h3>Social media posting</h3>
                <p>Instagram, X (Twitter), and LinkedIn re-compress every upload. Starting with a well-compressed source avoids the visible quality drop that comes from a large file being crushed by the platform.</p>
              </div>
              <div className="use-case">
                <h3>Portfolio and presentation images</h3>
                <p>A deck or PDF with 30 embedded images easily exceeds 50 MB. Compressing each image individually before embedding keeps the final file shareable.</p>
              </div>
            </div>
          </div>

          {/* UPDATED: FAQ expanded from 5 to 11 */}
          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>How much can I reduce image file size?</h3>
                <p>Most users achieve 70–90% file size reduction while maintaining excellent visual quality. Photographs typically compress better than graphics with sharp edges. At 80% quality, a 4 MB smartphone photo usually drops to 300–500 KB.</p>
              </div>
              <div className="faq-item">
                <h3>What image formats are supported?</h3>
                <p>The compressor accepts JPG, JPEG, PNG, WebP, GIF, and BMP for input. Output is delivered in the same format as the source, compressed. To convert between formats, use our <Link to="/image-converter/">Image Converter</Link>.</p>
              </div>
              <div className="faq-item">
                <h3>Will compression affect image quality?</h3>
                <p>At 80–90% quality, the human eye cannot detect a difference on any standard display — photo labs and professional publishers use these settings routinely. Below 70% you may start to see artifacts in smooth areas like sky gradients or skin tones. You can preview the result before downloading.</p>
              </div>
              <div className="faq-item">
                <h3>Are my images uploaded to a server?</h3>
                <p>No. All processing happens entirely in your browser using JavaScript. Your images never leave your device — no server receives them, nothing is stored, and there is no retention policy because nothing is retained. You can verify this in your browser's Network tab.</p>
              </div>
              <div className="faq-item">
                <h3>What quality setting should I use?</h3>
                <p>For web images, 80–90% quality gives the best balance of file size and visual quality. For print or archival purposes, use 90–95%. For maximum compression where some quality loss is acceptable — thumbnails, previews — 70–80% works well.</p>
              </div>
              <div className="faq-item">
                <h3>Can I compress multiple images at once?</h3>
                <p>The current version processes one image at a time for maximum control over quality settings. For a batch of similar images (for example, a set of product photos), the same slider setting usually works across all of them — re-compress each at 80% and download.</p>
              </div>
              <div className="faq-item">
                <h3>Does compressing an image twice reduce quality further?</h3>
                <p>Yes. Each lossy compression pass discards additional data from an already-degraded file. Compressing an image you received as an email attachment, or one that was already saved from a website, will produce visible artifacts without meaningful size savings. Always compress from the original when you have access to it.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference between lossy and lossless compression?</h3>
                <p>Lossy compression permanently removes data the eye is unlikely to notice, producing much smaller files. Lossless compression preserves every pixel exactly, producing larger files. This tool uses lossy compression — the right choice for photographs and web images. For logos, icons, and screenshots with text, use PNG or WebP lossless instead.</p>
              </div>
              <div className="faq-item">
                <h3>Can I compress a PNG without losing transparency?</h3>
                <p>Yes. PNG transparency is preserved by the compressor. However, PNG compression tends to be less dramatic than JPEG compression for the same image, because PNG's lossless nature limits how much data can be removed. For transparency plus strong compression, convert to WebP lossless using our <Link to="/image-converter/">Image Converter</Link> first.</p>
              </div>
              <div className="faq-item">
                <h3>What is a good file size for web images?</h3>
                <p>Hero and background images should stay under 200 KB. Inline content images should target 50–150 KB. Thumbnails under 50 KB. These targets keep Largest Contentful Paint (LCP) fast on mobile connections and directly help Core Web Vitals scores.</p>
              </div>
              <div className="faq-item">
                <h3>Does compression remove EXIF data (camera info, GPS location)?</h3>
                <p>Compression via canvas rendering naturally strips most EXIF metadata, including GPS coordinates, camera model, and timestamps — which is usually a privacy win. If you need to preserve EXIF for professional purposes, use dedicated software that offers metadata retention.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/image-converter/" className="related-tool">
                <div className="tool-icon">Image Converter</div>
                <div className="tool-description">Convert images between JPG, PNG, WebP, BMP, and GIF formats</div>
              </Link>
              <Link to="/qr-generator/" className="related-tool">
                <div className="tool-icon">QR Code Generator</div>
                <div className="tool-description">Generate QR codes for your images and share them easily</div>
              </Link>
              <Link to="/json-formatter/" className="related-tool">
                <div className="tool-icon">JSON Formatter</div>
                <div className="tool-description">Format and validate JSON data for your web applications</div>
              </Link>
              <Link to="/yt-thumbnail/" className="related-tool">
                <div className="tool-icon">YouTube Thumbnail Downloader</div>
                <div className="tool-description">Download YouTube video thumbnails in full resolution</div>
              </Link>
              <Link to="/blog/how-to-compress-images-without-losing-quality/" className="related-tool">
                <div className="tool-icon">How to Compress Images Without Losing Quality</div>
                <div className="tool-description">Professional techniques for balancing file size and image quality</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default ImageCompressorPage